import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ClassCard = ({ 
  classData, 
  onBook, 
  onViewDetails,
  isBooked = false 
}) => {
  const {
    id,
    name,
    instructor,
    instructorImage,
    instructorImageAlt,
    time,
    duration,
    difficulty,
    spotsLeft,
    totalSpots,
    category,
    categoryColor
  } = classData;

  const getDifficultyColor = (level) => {
    const colors = {
      'Beginner': 'text-success bg-success/10',
      'Intermediate': 'text-warning bg-warning/10',
      'Advanced': 'text-error bg-error/10'
    };
    return colors?.[level] || colors?.['Beginner'];
  };

  const isAlmostFull = spotsLeft <= 3 && spotsLeft > 0;
  const isFull = spotsLeft === 0;

  return (
    <div className="bg-card border border-border rounded-lg shadow-md overflow-hidden transition-smooth hover-lift">
      <div 
        className="h-2"
        style={{ backgroundColor: categoryColor }}
        aria-hidden="true"
      />
      
      <div className="p-4 md:p-5 lg:p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-1 line-clamp-1">
              {name}
            </h3>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="Clock" size={16} />
              <span className="whitespace-nowrap">{time}</span>
              <span className="text-border">•</span>
              <span className="whitespace-nowrap">{duration} min</span>
            </div>
          </div>
          
          <span className={`px-3 py-1 rounded-full text-xs font-medium caption whitespace-nowrap ${getDifficultyColor(difficulty)}`}>
            {difficulty}
          </span>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="relative w-10 h-10 md:w-12 md:h-12 flex-shrink-0">
            <Image
              src={instructorImage}
              alt={instructorImageAlt}
              className="w-full h-full rounded-full object-cover"
            />
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-success rounded-full border-2 border-card" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground line-clamp-1">
              {instructor}
            </p>
            <p className="text-xs text-muted-foreground line-clamp-1">
              {category}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon 
              name="Users" 
              size={16} 
              color={isFull ? 'var(--color-error)' : isAlmostFull ? 'var(--color-warning)' : 'var(--color-muted-foreground)'} 
            />
            <span className={`text-sm font-medium data-text ${isFull ? 'text-error' : isAlmostFull ? 'text-warning' : 'text-foreground'}`}>
              {spotsLeft} / {totalSpots} spots
            </span>
          </div>
          
          {isBooked && (
            <div className="flex items-center gap-1 text-success">
              <Icon name="CheckCircle2" size={16} />
              <span className="text-xs font-medium caption">Booked</span>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewDetails(id)}
            className="flex-1"
            iconName="Info"
            iconPosition="left"
            iconSize={16}
          >
            Details
          </Button>
          
          {!isBooked && (
            <Button
              variant={isFull ? "secondary" : "default"}
              size="sm"
              onClick={() => onBook(id)}
              disabled={isFull}
              className="flex-1"
              iconName={isFull ? "Clock" : "Calendar"}
              iconPosition="left"
              iconSize={16}
            >
              {isFull ? 'Waitlist' : 'Book'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClassCard;